/**
 * Helper function to return proper path in data for liquid
 * It transforms schema paths into liquid paths
 * @param {*} strToReplace
 * @param {*} forWhat
 */
const solveForWhat = (strToReplace, forWhat) => {
	if (!strToReplace) return ""

	// Policy
	if (forWhat === "policies") {
		// 1 Replace case with new path for policy
		// 2 Replace holder, insureds, otherHolders --> contractHolder, contractInsured, otherContractHolders
		// We have to be careful to find these strings properly so that we don't replace some parts that shoudln't be replaced
		// that's why we use /\.holder/
		return strToReplace
			.replace(/case\./g, `data.policies.list[0]`)
			.replace(/\.holder/g, `.contractHolder`)
			.replace(/\.insureds/g, `.contractInsured`)
			.replace(/\.otherHolders/g, `.otherContractHolders`)
	}

	// Case
	if (forWhat === "cases")
		return strToReplace.replace(/case\./g, `data.cases.list[0].`)
}

/**
 * Entity
 */
const getEntity = (field, forWhat = "cases") => {
	let output = ""
	output += `{{- ${field.bindTo} -}}`
	output = solveForWhat(output, forWhat) // Process forWhat
	return output
}

/**
 * Entity Field
 */
const getEntityField = (field, forWhat = "cases") => {
	// If entity is coming from relationship
	// We have to first find relationship and after that find entity
	if (field?.entity?.getEntityFromRelationship) {
		const {
			caseFactTypeAsRefId,
			whichRelationships
		} = field?.entity?.getEntityFromRelationship

		// Compose liquid string
		let output = ""
		output += `{%- assign relationshipId = case.facts | where:"type", "${caseFactTypeAsRefId}" -%}` //{%- assign fact = data.cases.list[0].holder.facts | where:"id", refId[0].value.stringValue -%}
		output += `{%- assign relationship =  ${whichRelationships}  | where: "id", relationshipId[0].value.stringValue  -%}`
		output += `{{- relationship[0].entity.${field.fieldToUpdate} -}}`
		output = solveForWhat(output, forWhat) // Process forWhat
		return output
	}

	// Entity is coming either from holder, insureds or otherHolders
	let output = ""
	output += `{{- ${field.entity}.${field.fieldToUpdate} -}}`
	output = solveForWhat(output, forWhat) // Process forWhat
	return output
}

/**
 * Case Fact Normal
 */
const getCaseFact = (field, forWhat = "cases") => {
	const dynamicType = field?.dynamicType
	const bindTo = field?.bindTo?.replace(`.${dynamicType}`, "") // get rid of dynamicType
	const dataType = `${field.dataType || "string"}Value`

	// Compose liquid string
	let output = ""
	output += `{%- assign fact = ${bindTo} | where:"type", "${dynamicType}" -%}` //{%- assign fact = data.cases.list[0].holder.facts | where:"id", refId[0].value.stringValue -%}
	output += `{{- fact[0].value.${dataType} -}}`
	output = solveForWhat(output, forWhat) // Process forWhat
	return output
}

/**
 * Case Fact via Ref
 */
const getCaseFactWithRef = (field, forWhat = "cases") => {
	/**
	 * Helper function to determine what dataType to display
	 * Takes into account component
	 * @param {} field
	 */
	const getDataType = field => {
		const dataType = field?.props?.createOptions?.dataType || "string"

		// For fact, we have to return proper dataType
		if (field.component === "entity-fact")
			return `.value.${dataType || "string"}Value`

		// For relationship, as default we return entity name
		if (field.component === "entity-relationship") return `.entity.name`

		// For contacts, we return value
		if (field.component === "entity-contact") return `.value`
	}

	// 1: If entity is coming from relationship
	if (field?.entity?.getEntityFromRelationship) {
		const {
			caseFactTypeAsRefId,
			whichRelationships
		} = field?.entity?.getEntityFromRelationship
		const { fieldOfEntityFromRelationship } = field.relation
		const dynamicType = field.dynamicType
		const dataType = getDataType(field)

		// TODO: we should handle nested situation when entity comes from relationship of entity in relationship

		// Compose liquid string
		let output = ""
		output += `{%- assign relationshipId = case.facts | where:"type", "${caseFactTypeAsRefId}" -%}`
		output += `{%- assign refId = case.facts | where:"type", "${dynamicType}" -%}`
		output += `{%- assign relationship =  ${whichRelationships}  | where: "id", relationshipId[0].value.stringValue  -%}`
		output += `{%- assign refField = relationship[0].entity.${fieldOfEntityFromRelationship} | where:"id", refId[0].value.stringValue -%}`
		output += `{{- refField[0]${dataType} -}}`
		output = solveForWhat(output, forWhat) // Process forWhat
		return output
	}

	// 2: If entity is not in relationship
	const dynamicType = field?.dynamicType
	const bindTo = field?.bindTo?.replace(`.${dynamicType}`, "") // get rid of dynamic type
	const dataType = getDataType(field)

	// Compose liquid string
	let output = ""
	output += `{%- assign refId = ${bindTo} | where:"type","${dynamicType}" -%}`
	output += `{%- assign refField = ${field.relation} | where:"id", refId[0].value.stringValue -%}` //{%- assign fact = data.cases.list[0].holder.facts | where:"id", refId[0].value.stringValue -%}
	output += `{{- refField[0]${dataType} -}}`
	output = solveForWhat(output, forWhat) // Process forWhat
	return output
}

/**
 * Case Fact With Ref For Address
 */
const getCaseFactWithRefForAddress = (field, forWhat = "cases") => {
	console.log("executing getCaseFactWithRefForAddress")

	// If entity is coming from relationship
	// We have to first find relationship and after that find entity
	if (field?.entity?.getEntityFromRelationship) {
		const {
			caseFactTypeAsRefId,
			whichRelationships
		} = field?.entity?.getEntityFromRelationship
		const dynamicType = field.dynamicType // end of bindTo
		const { fieldOfEntityFromRelationship } = field.relation

		// TODO: we should handle nested situation when entity comes from relationship of entity in relationship

		// Compose liquid string
		let output = ""
		output += `{%- assign relationshipId = case.facts | where:"type", "${caseFactTypeAsRefId}" -%}`
		output += `{%- assign refId = case.facts | where:"type", "${dynamicType}" -%}`
		output += `{%- assign relationship =  ${whichRelationships}  | where: "id", relationshipId[0].value.stringValue  -%}`
		output += `{%- assign address = relationship[0].entity.${fieldOfEntityFromRelationship} | where:"id", refId[0].value.stringValue -%}`
		// HACK: this schema is hadcoded but should technically come from schema def for creation of address taht should be on field
		output += `{%- assign addressLine1 = address[0].fields2 | where: "type", "addressLine1" -%}`
		output += `{%- assign addressLine2 = address[0].fields2 | where: "type", "addressLine2" -%}`
		output += `{%- assign addressLine3 = address[0].fields2 | where: "type", "addressLine3" -%}`
		output += `{%- assign floor = address[0].fields2 | where: "type", "floor" -%}`
		output += `{%- assign zipCode = address[0].fields2 | where: "type", "zipCode" -%}`
		output += `{%- assign city = address[0].fields2 | where: "type", "city" -%}`
		output += `{%- assign county = address[0].fields2 | where: "type", "county" -%}`
		output += `{%- assign country = address[0].fields2 | where: "type", "country" -%}`
		output += `{{- addressLine1[0].value -}}, `
		output += `{{- addressLine2[0].value -}}, `
		output += `{{- addressLine3[0].value -}}, `
		output += `{{- floor[0].value -}}, `
		output += `{{- zipCode[0].value -}}, `
		output += `{{- city[0].value -}}, `
		output += `{{- county[0].value -}}, `
		output += `{{- country[0].value -}}`
		output = solveForWhat(output, forWhat) // Process forWhat
		return output
	}

	// If entity is not coming from relationships
	// Compose liquid string
	let output = ""
	output += `{%- assign refId = case.facts | where:"type","${field.dynamicType}" -%}`
	output += `{%- assign address = ${field.relation} | where:"id", refId[0].value.stringValue -%}` //{%- assign fact = data.cases.list[0].holder.facts | where:"id", refId[0].value.stringValue -%}
	// HACK: this schema is hadcoded but should technically come from schema def for creation of address taht should be on field
	output += `{%- assign addressLine1 = address[0].fields2 | where: "type", "addressLine1" -%}`
	output += `{%- assign addressLine2 = address[0].fields2 | where: "type", "addressLine2" -%}`
	output += `{%- assign addressLine3 = address[0].fields2 | where: "type", "addressLine3" -%}`
	output += `{%- assign floor = address[0].fields2 | where: "type", "floor" -%}`
	output += `{%- assign zipCode = address[0].fields2 | where: "type", "zipCode" -%}`
	output += `{%- assign city = address[0].fields2 | where: "type", "city" -%}`
	output += `{%- assign county = address[0].fields2 | where: "type", "county" -%}`
	output += `{%- assign country = address[0].fields2 | where: "type", "country" -%}`
	output += `{{- addressLine1[0].value -}}, `
	output += `{{- addressLine2[0].value -}}, `
	output += `{{- addressLine3[0].value -}}, `
	output += `{{- floor[0].value -}}, `
	output += `{{- zipCode[0].value -}}, `
	output += `{{- city[0].value -}}, `
	output += `{{- county[0].value -}}, `
	output += `{{- country[0].value -}}`
	output = solveForWhat(output, forWhat) // Process forWhat
	return output
}

/**
 * getOfferFact
 */
const getOfferFact = (field, forWhat = "cases") => {
	console.log("executing getOfferFact ")
	const dynamicType = field.model // old shema is using field.model
	const dataType = `${field.dataType || "string"}Value`

	// Decide path based on forWhat
	// If cases, we are accessing offers from cases
	// If policies, we are actually accessing policy facts instead of an offer
	let p
	if (forWhat === "cases") p = "data.cases.list[0].proposals[0].basket[0].facts"
	if (forWhat === "policies") p = "data.policies.list[0].facts"

	let output = ""
	output += `{%- assign field = ${p} | where:"type","${dynamicType}" -%}`
	output += `{{- field[0].value.${dataType} -}}`
	return output
}

/**
 * getOfferField
 */
const getOfferField = (field, forWhat) => {
	console.log("executing getOfferField", field)
	const dynamicType = field.model // old shema is using field.model

	// Decide path based on forWhat
	// If cases, we are accessing offers from cases
	// If policies, we are actually accessing policy facts instead of an offer
	let p
	if (forWhat === "cases") p = "data.cases.list[0].proposals[0].basket[0]"
	if (forWhat === "policies") p = "data.policies.list[0]"
	return `{{- ${p}.${dynamicType} -}}`
}

/**
 * Helper functions to generate liquid syntax based on schema definition we have for case-form
 * Decide what liquid to generate
 * Order is important to eliminate choices
 * @param {object} field - field from schema
 * @param {string} forWhat - either `cases` or `policies`
 */
const generateLiquid = (field, forWhat = "cases") => {
	// ###########################
	// 1: First eliminate offers
	// ###########################

	// Offer Fact
	if (field?.bindTo?.includes("offer.facts"))
		return getOfferFact(field, forWhat)

	// Offer Field
	if (field?.bindTo?.includes("offer.")) return getOfferField(field, forWhat)

	// ###########################
	// 2: Secondly take care of case
	// ###########################
	// Handle custom injected components, for now it means it's caseFact without reference
	if (field?.componentObject) return getCaseFact(field, forWhat)

	// Entity Select
	if (field?.component === "entity-select") return getEntity(field, forWhat)

	// Entity Field
	if (field?.component === "entity-field") return getEntityField(field, forWhat)

	// Case fact with ref for address
	if (field?.component === "entity-address")
		return getCaseFactWithRefForAddress(field, forWhat)

	// Case Fact with reference
	if (field?.component?.includes("entity-"))
		return getCaseFactWithRef(field, forWhat)

	// Case Fact without ref
	if (
		field?.bindTo?.includes("case.facts") &&
		!field?.component?.includes("entity-")
	)
		return getCaseFact(field, forWhat)
}

export { generateLiquid }
